import React, { useState, useEffect } from "react";
import { Select, DatePicker } from "antd";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const fieldKeyMap = {
  due: "due_date",
  start: "start_date",
  payment: "payment_date",
  next: "next_due_date",
};

const getPresetRange = (val) => {
  switch (val) {
    case "today":
      return [
        dayjs().startOf("day").format("YYYY-MM-DD"),
        dayjs().endOf("day").format("YYYY-MM-DD"),
      ];
    case "yesterday":
      return [
        dayjs().subtract(1, "day").startOf("day").format("YYYY-MM-DD"),
        dayjs().subtract(1, "day").endOf("day").format("YYYY-MM-DD"),
      ];
    case "this_week":
      return [
        dayjs().startOf("week").format("YYYY-MM-DD"),
        dayjs().endOf("week").format("YYYY-MM-DD"),
      ];
    case "last_week":
      return [
        dayjs().subtract(1, "week").startOf("week").format("YYYY-MM-DD"),
        dayjs().subtract(1, "week").endOf("week").format("YYYY-MM-DD"),
      ];
    case "this_month":
      return [
        dayjs().startOf("month").format("YYYY-MM-DD"),
        dayjs().endOf("month").format("YYYY-MM-DD"),
      ];
    case "last_month":
      return [
        dayjs().subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        dayjs().subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      ];
    default:
      return null;
  }
};

export default function DateFilterPicker({ dateFilter, setDateFilter }) {
  const [mode, setMode] = useState("preset");
  const [preset, setPreset] = useState("");

  // sync UI with cleared dates
  useEffect(() => {
    if (!dateFilter.from && !dateFilter.to) {
      setPreset("");
    }
  }, [dateFilter.from, dateFilter.to]);

  // date type change
  const handleFieldChange = (val) => {
    setDateFilter({
      date_type: fieldKeyMap[val],
      from: "",
      to: "",
    });
  };

  // preset change
  const handlePresetChange = (val) => {
    setPreset(val);
    const range = getPresetRange(val);
    if (range) {
      setDateFilter((prev) => ({
        ...prev,
        from: range[0],
        to: range[1],
      }));
    } else {
      // reset if "Select Preset Range"
      setDateFilter((prev) => ({ ...prev, from: "", to: "" }));
    }
  };

  // custom range change
  const handleCustomChange = (dates) => {
    if (!dates || dates.length === 0) {
      setDateFilter((prev) => ({ ...prev, from: "", to: "" }));
      return;
    }
    setDateFilter((prev) => ({
      ...prev,
      from: dates[0]?.startOf("day").format("YYYY-MM-DD"),
      to: dates[1]?.endOf("day").format("YYYY-MM-DD"),
    }));
  };

  return (
    <div style={{ display: "flex", gap: 8 }}>
      {/* 1st: Date type */}
      <Select
        value={
          Object.keys(fieldKeyMap).find(
            (key) => fieldKeyMap[key] === dateFilter.date_type
          ) || "due"
        }
        style={{ width: 150 }}
        onChange={handleFieldChange}
        options={[
          { value: "due", label: "Due Date" },
          { value: "start", label: "Start Date" },
          { value: "payment", label: "Payment Date" },
          { value: "next", label: "Next Due Date" },
        ]}
      />

      {/* 2nd: Mode */}
      <Select
        value={mode}
        style={{ width: 120 }}
        onChange={setMode}
        options={[
          { value: "preset", label: "Preset" },
          { value: "custom", label: "Custom" },
        ]}
      />

      {/* 3rd: Preset / Custom Control */}
      {mode === "preset" ? (
        <Select
          value={preset || undefined}
          style={{ width: 220 }}
          placeholder="Select Preset Range"
          onChange={handlePresetChange}
          options={[
            { value: "today", label: "Today" },
            { value: "yesterday", label: "Yesterday" },
            { value: "this_week", label: "This Week" },
            { value: "last_week", label: "Last Week" },
            { value: "this_month", label: "This Month" },
            { value: "last_month", label: "Last Month" },
          ]}
        />
      ) : (
        <RangePicker
          value={
            dateFilter.from && dateFilter.to
              ? [dayjs(dateFilter.from), dayjs(dateFilter.to)]
              : []
          }
          onChange={handleCustomChange}
          style={{ width: 220, height: 32 }}
        />
      )}
    </div>
  );
}
